// Pure nav over the positioned song lattice: arrow/D-pad keys move focus to the
// nearest node in that direction; select loads a single-song node or opens a
// multi-song node's rail (then up/down pick, select loads, left/right back). No DOM.
import type { NavKey } from "../input/command-layer";
import type { LayoutNode, MapLayout } from "../songs/map-layout";

export interface MapNavState {
  mode: "graph" | "rail";
  nodeId: string;
  songIndex: number;
}
interface MapNavResult {
  state: MapNavState;
  load?: string;
}

const clamp = (i: number, n: number): number => Math.max(0, Math.min(n - 1, i));

// Nearest node in the half-plane for `key`, scoring along-axis distance + a cross-axis
// penalty so movement stays roughly straight.
function nearest(from: LayoutNode, key: NavKey, nodes: LayoutNode[]): LayoutNode | undefined {
  const inDir = (t: LayoutNode): boolean => {
    if (key === "up") return t.y < from.y;
    if (key === "down") return t.y > from.y;
    if (key === "left") return t.x < from.x;
    if (key === "right") return t.x > from.x;
    return false;
  };
  const cost = (t: LayoutNode): number => {
    const dx = Math.abs(t.x - from.x);
    const dy = Math.abs(t.y - from.y);
    const along = key === "up" || key === "down" ? dy : dx;
    const cross = key === "up" || key === "down" ? dx : dy;
    return along + cross * 2;
  };
  return nodes
    .filter((t) => t.id !== from.id && inDir(t))
    .sort((a, b) => cost(a) - cost(b) || a.id.localeCompare(b.id))[0];
}

export function mapNavReduce(state: MapNavState, key: NavKey, layout: MapLayout): MapNavResult {
  const node = layout.nodes.find((x) => x.id === state.nodeId);
  if (!node) return { state };

  if (state.mode === "rail") {
    switch (key) {
      case "up":
        return { state: { ...state, songIndex: clamp(state.songIndex - 1, node.songIds.length) } };
      case "down":
        return { state: { ...state, songIndex: clamp(state.songIndex + 1, node.songIds.length) } };
      case "left":
      case "right":
        return { state: { ...state, mode: "graph" } };
      case "select":
        return { state, load: node.songIds[state.songIndex] };
    }
  }

  if (key === "select") {
    if (node.songIds.length === 1) return { state, load: node.songIds[0] };
    if (node.songIds.length > 1) return { state: { ...state, mode: "rail", songIndex: 0 } };
    return { state };
  }
  const target = nearest(node, key, layout.nodes);
  return target ? { state: { ...state, nodeId: target.id } } : { state };
}
